import React from "react";
import {useServices} from "../../../hooks/use-services.hook";
import {observer} from "mobx-react";
import styled from "styled-components";
import {VoucherCodeComponent} from "./voucher-code/voucher-code.component";
import {BookingFlowPage} from "../booking-flow.page";
import {InvoiceViewContainerComponent} from "./invoice-view-container.component";
import {PaymentMethodsTabsComponent} from "./tabs/payment-methods-tabs.component";
import {PaymentPageFooterComponent} from "./payment-page-footer.component";
import {useScreenMediaQueriesChecks} from "../../../hooks/use-screen-media-queries-checks.hook";
import {OrganizationCreditPaymentMethodComponent} from "./organization-credit/organization-credit-payment-method.component";
import {HoldBookingComponent} from "./hold-booking/hold-booking.component";
import {
    PsoWorkersAgreementsComponent
} from "../../../components/territorial-continuity-regime/agreements/workers/pso-workers-agreements.component";
import {
    PsoFamilyAgreementsComponent
} from "../../../components/territorial-continuity-regime/agreements/families/pso-family-agreements.component";


const PAYMENT_PAGE_FOOTER_CSS_CLASS = 'payment-page-footer'



const PaymentMethodGroupsContainerBox = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${props => props.theme.spacing.spacing30};
`


const PageTitleWhenInsuranceIsPresentBox = styled.div`
    font-size: ${props => props.theme.fontSize.h1};
    font-weight: ${props => props.theme.fontWeight.bold};
`

const PaymentPageContentComponent: React.FC = observer(() => {
    const services = useServices();
    const booking = services.booking.current;
    const insurancePaymentPageStrategy = booking.insurance.insuranceInPaymentPageStrategy;

    const renderPageTitleWhenInsuranceIsPresent = () => {

        if(!insurancePaymentPageStrategy) {
            return null;
        }

        return (
            <PageTitleWhenInsuranceIsPresentBox>
                {services.language.translate('Payment')}
            </PageTitleWhenInsuranceIsPresentBox>
        )

    }

    const renderInsuranceQuotes = () => {
        if(!insurancePaymentPageStrategy) {
            return null;
        }

        return insurancePaymentPageStrategy.renderComponent();
    }

    return (
        <PaymentMethodGroupsContainerBox>

            {renderInsuranceQuotes()}

            {renderPageTitleWhenInsuranceIsPresent()}

            <HoldBookingComponent/>

            <OrganizationCreditPaymentMethodComponent organizationCredit={booking.paymentHandler.organizationCredit}/>

            <PaymentMethodsTabsComponent paymentHandler={booking.paymentHandler} />

            <VoucherCodeComponent voucher={booking.paymentHandler.voucher} />

            <InvoiceViewContainerComponent/>

        </PaymentMethodGroupsContainerBox>
    );
});


const PageBox = styled(BookingFlowPage)`
    & .${PAYMENT_PAGE_FOOTER_CSS_CLASS} {
        background-color: ${props => props.theme.colors.successVeryLight};
    }
`

export const PaymentPage: React.FC = observer(() => {
    const services = useServices();
    const screenMediaQuery = useScreenMediaQueriesChecks();



    const renderContent = () => {
        const pso = services.booking.current.pso;
        if(pso.hasValidPsoAgreements) {
            return (
                <PaymentPageContentComponent/>
            );
        } else {
            if(pso.workersAgreements.agreements.length > 0) {
                return (<PsoWorkersAgreementsComponent workersAgreements={pso.workersAgreements}/>)
            } else if(pso.familyAgreements.agreements.length > 0) {
                return (<PsoFamilyAgreementsComponent familyAgreements={pso.familyAgreements}/>)
            } else {
                return (
                    //just in case
                    <PaymentPageContentComponent/>
                );
            }


        }

    }

    const getFooterRenderer = () => {
        return () => {
            return (<PaymentPageFooterComponent/>);
        }
    }

    const replaceContinueButton = () => {
        if(!screenMediaQuery.smallScreen) {
            return undefined;
        }

        return () => services.booking.current.paymentHandler.renderPayButton();

    }

    const getPageTitle = () => {
        const standardPageTitle = services.language.translate('Payment');
        if(!services.booking.currentPageCanBeRendered) {
            return standardPageTitle;
        }

        const insuranceInPaymentPageStrategy = services.booking.current.insurance.insuranceInPaymentPageStrategy;

        if(insuranceInPaymentPageStrategy) {
            return insuranceInPaymentPageStrategy.paymentPageTitle;
        }

        return standardPageTitle;

    }


    return (
        <PageBox disableSwipeNavigation={true}
                         headerOptions={{
                            title: getPageTitle()
                         }}
                         contentOptions={{
                             renderContent: renderContent,
                             maxContentWidth: '1280px',
                             hideCopyRight: true,
                             hidePayPalPayLater: true,
                         }}
                         footerOptions={{
                             hideFooter: !services.booking.currentPageCanBeRendered || !services.booking.current.pso.hasValidPsoAgreements,
                             renderExtraFooterContent: getFooterRenderer(),
                             pageFooterClassName: PAYMENT_PAGE_FOOTER_CSS_CLASS
                         }}
                         shoppingCartOptions={{
                             hideContinueButton: true,
                             showPromotionCode: false,
                             replaceContinueButton: replaceContinueButton()
                         }}/>
    );
});
